import _ from 'lodash';
import {
	BooleanPropertyType,
	DateTimePropertyType,
	GenericSingleProperty,
	GenericSinglePropertyType,
	NestedProperty,
	NOBPropertyType,
	NumericPropertyType,
	Property,
	PropertyRaw,
	SingleProperty,
	TextPropertyType,
} from '../types/property.types';
import {
	Capabilities,
	Capability,
} from '../types/capabilities/capabilities.property.types';
import { Operators } from '../types/operators.types';
import { getOperatorsForType } from './operators';

/**
 * Maps a string property type to the corresponding enum value
 * @internal
 * @param type - The string property type
 * @returns The corresponding enum property type
 */
const mapPropertyTypeToEnum = (type: string): GenericSinglePropertyType => {
	switch (type) {
		case 'Text':
			return TextPropertyType.Text;
		case 'Memo':
			return TextPropertyType.Memo;
		case 'Double':
			return NumericPropertyType.Double;
		case 'Integer':
			return NumericPropertyType.Integer;
		case 'Long Integer':
			return NumericPropertyType.LongInteger;
		case 'Boolean':
			return BooleanPropertyType.Boolean;
		case 'Date / Time':
			return DateTimePropertyType.DateTime;
		case 'NOB':
			return NOBPropertyType.NOB;
		default:
			throw new Error(`Unknown property type: ${type}`);
	}
};

/**
 * Creates a new property based on the provided parameters.
 * @param params - The raw property parameters.
 * @returns A new property instance.
 */
export const newProperty = (params: PropertyRaw): Property => {
	if (params.Type === 'List') {
		return new NestedPropertyImpl(params);
	} else {
		return new GenericSinglePropertyImpl(params);
	}
};

/**
 * Implementation of a generic single property.
 */
export class GenericSinglePropertyImpl implements GenericSingleProperty {
	id: string;
	type: GenericSinglePropertyType;
	displayName: string;
	subType?: string;
	subSubType?: string;
	precision?: number;
	units?: string;
	unitsDescription?: string;
	unitsCode?: string;
	deprecatedFieldName?: string;
	category?: string;
	required: boolean;
	dbFieldNameOverride?: string;
	cachedFieldNameOverride?: string;
	path: string[];
	private readonly capabilities: Capability[];

	/**
	 * Constructs a new GenericSinglePropertyImpl instance.
	 * @internal
	 * @param params - The property schema parameters.
	 */
	constructor(params: PropertyRaw) {
		this.id = params.Property;
		this.deprecatedFieldName = params.DeprecatedFieldName;
		this.type = mapPropertyTypeToEnum(params.Type);
		this.subType = params.SubType;
		this.subSubType = params.SubSubType;
		this.displayName = params.DisplayName;
		this.precision = params.Precision;
		this.units = params.Units;
		this.unitsDescription = params.UnitsDescription;
		this.unitsCode = params.UnitsCode;
		this.category = params.Category;
		this.required = params.Required === true;
		this.dbFieldNameOverride = params.DbFieldNameOverride;
		this.cachedFieldNameOverride = params.CachedFieldNameOverride;
		this.path = params.Path ?? [params.Property];

		this.capabilities = [];

		for (const capability of Capabilities) {
			if (params[capability]) {
				this.capabilities.push(capability);
			}
		}
	}

	/**
	 * Checks if the property has a specific capability.
	 * @param capability - The capability to check.
	 * @returns True if the property has the capability, false otherwise.
	 */
	hasCapability(capability: Capability): boolean {
		return this.capabilities.includes(capability);
	}

	/**
	 * Gets the available operators for this property.
	 * This is useful for creating filters, queries, and UI components.
	 * @returns The available operators for this property.
	 */
	getOperators(): Operators {
		return getOperatorsForType(this.type, {
			unitsCode: this.unitsCode,
			subType: this.subType,
		});
	}

	/**
	 * Gets the database field name for the property.
	 */
	get databaseField(): string {
		return this.dbFieldNameOverride ?? _.toUpper(_.snakeCase(this.id));
	}

	/**
	 * Gets the cached field name for the property.
	 */
	get cachedField(): string {
		return this.cachedFieldNameOverride ?? this.id;
	}
}

/**
 * Implementation of a nested property.
 */
export class NestedPropertyImpl implements NestedProperty {
	type: 'List';
	properties: SingleProperty[];
	id: string;
	deprecatedFieldName?: string;
	displayName: string;
	category?: string;
	required: boolean;
	dbFieldNameOverride?: string;
	cachedFieldNameOverride?: string;
	path: string[];

	/**
	 * Constructs a new NestedPropertyImpl instance.
	 * @internal
	 * @param params - The raw property parameters.
	 */
	constructor(params: PropertyRaw) {
		this.id = params.Property;
		this.deprecatedFieldName = params.DeprecatedFieldName;
		this.type = 'List';
		this.displayName = params.DisplayName;
		this.category = params.Category;
		this.required = params.Required === true;
		this.path = params.Path ?? [params.Property];
		this.properties =
			params.Properties?.map(rawProperty =>
				newProperty({
					...rawProperty,
					Path: rawProperty.Path ?? [
						...this.path,
						rawProperty.Property,
					],
				}),
			).filter(p => p.type !== 'List') || [];
		this.dbFieldNameOverride = params.DbFieldNameOverride;
		this.cachedFieldNameOverride = params.CachedFieldNameOverride;
	}

	/**
	 * Converts a single property to its raw representation.
	 * @internal
	 * @param property - The single property to convert.
	 * @returns The raw property representation.
	 */
	private convertToPropertyRaw(property: SingleProperty): PropertyRaw {
		return {
			Property: property.id,
			DeprecatedFieldName: property.deprecatedFieldName,
			Type: property.type,
			SubType: property.subType,
			SubSubType: property.subSubType,
			DisplayName: property.displayName,
			Precision: property.precision,
			Units: property.units,
			UnitsDescription: property.unitsDescription,
			UnitsCode: property.unitsCode,
			Lof: property.hasCapability('Lof'),
			Cof: property.hasCapability('Cof'),
			Rehab: property.hasCapability('Rehab'),
			ShowOnMapPanel: property.hasCapability('ShowOnMapPanel'),
			Category: property.category,
			DbFieldNameOverride: property.dbFieldNameOverride,
			CachedFieldNameOverride: property.cachedFieldNameOverride,
			Path: property.path,
		};
	}

	/**
	 * Creates a copy of the nested property with new properties.
	 * @param properties - The new properties to include.
	 * @returns A new NestedProperty instance.
	 */
	copyWithNewProperties(properties: SingleProperty[]): NestedProperty {
		return new NestedPropertyImpl({
			Property: this.id,
			DeprecatedFieldName: this.deprecatedFieldName,
			Type: this.type,
			DisplayName: this.displayName,
			Category: this.category,
			Properties: properties.map(this.convertToPropertyRaw),
			DbFieldNameOverride: this.dbFieldNameOverride,
			CachedFieldNameOverride: this.cachedFieldNameOverride,
			Path: this.path,
		});
	}

	/**
	 * Gets the database field name for the property.
	 */
	get databaseField(): string {
		return this.dbFieldNameOverride ?? _.toUpper(_.snakeCase(this.id));
	}

	/**
	 * Gets the cached field name for the property.
	 */
	get cachedField(): string {
		return this.cachedFieldNameOverride ?? this.id;
	}
}
